import type {UsePopularFilterItems} from '@/modules/common/hooks/usePopularFilters';
import type {Filter} from '@/modules/common/models/filters';
import type {ExtractPropsWithoutRef} from '@/types';

import {ChipGroup} from '@travel-frontend/ui-components';
import {sortBy} from 'lodash';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {FilterHeadline} from '@/components/FilterHeadline';
import {
  Container,
  FilterTrigger,
  Headline,
  Item,
} from '@/components/PopularFilters/components';
import {testId} from '@/utils/testId';

export type PopularFiltersProps = ExtractPropsWithoutRef<typeof Container> & {
  items: UsePopularFilterItems;
  isSmallView?: boolean;
  onClickFilterTrigger?: React.MouseEventHandler<HTMLButtonElement>;
  handleChange: (
    filter: {
      id: Filter['id'];
      value: Filter['value'];
      setValue: Filter['setValue'];
    },
    popular: boolean
  ) => (event: {
    type: 'clear' | 'pop' | 'set' | 'push';
    value: null | number | string | Array<string>;
  }) => void;
};

export const PopularFilters: React.FunctionComponent<PopularFiltersProps> = ({
  items,
  isSmallView,
  handleChange,
  onClickFilterTrigger,
  ...rest
}) => {
  const {t} = useTranslation('filters');
  const [initialActiveItemIds, setInitialActiveItemIds] = useState<
    Array<string>
  >([]);

  const spacing = isSmallView ? '12px' : '16px';
  const chipGroupOverflow = isSmallView ? 'scroll' : 'wrap';

  // @FIXME there should be no need for an effect, because we know statically which filters are active (URL)
  // But that requires a bit more refactoring effort.
  useEffect(() => {
    if (items.length > 0 && initialActiveItemIds.length === 0) {
      setInitialActiveItemIds(
        items.filter((item) => item.active).map((item) => item.id)
      );
    }
  }, [items, initialActiveItemIds.length]);

  if (items.length === 0) {
    return null;
  }

  const sortedItems = sortBy(items, (item) => {
    if (initialActiveItemIds.includes(item.id)) {
      return -1;
    }
    return 0;
  });

  return (
    <Container {...rest} spacing={spacing} isSwipable={isSmallView}>
      <Headline>
        <FilterHeadline>{t('popularFilters')}</FilterHeadline>
        {isSmallView && (
          <FilterTrigger
            aria-label={t('buttons.filter')}
            category="secondary"
            variant="interaction"
            onClick={onClickFilterTrigger}
            data-testid={testId('filter-open')}
          >
            {t('allFilters')}
          </FilterTrigger>
        )}
      </Headline>
      <ChipGroup overflow={chipGroupOverflow}>
        {sortedItems.map(
          ({id, active, label, filter, value, type, disabled}) => (
            <Item
              id={id}
              key={id}
              active={active}
              label={label}
              filter={filter}
              value={value}
              type={type}
              disabled={disabled}
              handleChange={(filterCategory) => {
                return handleChange(filterCategory, true);
              }}
            />
          )
        )}
      </ChipGroup>
    </Container>
  );
};
